import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, ArrowRight, Tag } from 'lucide-react';
import { BACKEND_URL } from '../utils/api';
import INDUSTRIES from '../data/industries';
import SEOHead, { createBreadcrumbSchema, createOrganizationSchema } from './SEOHead';

const COLORS = { primary: '#002FA7', accent: '#FFCC00', dark: '#0A1628', light: '#F8FAFC' };

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [industry, setIndustry] = useState(searchParams.get('industry') || '');
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/blog/industries`).then(r => r.json()).then(d => setIndustries(d.industries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    const skip = (page - 1) * 12;
    params.set('skip', skip);
    params.set('limit', '12');

    fetch(`${BACKEND_URL}/api/blog/posts?${params}`)
      .then(r => r.json())
      .then(d => {
        let filteredPosts = d.posts || [];
        if (industry) {
          filteredPosts = filteredPosts.filter(p => p.industry === industry);
        }
        setPosts(filteredPosts);
        setTotal(d.total || 0);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [page, industry]);

  const schema = [
    createOrganizationSchema(),
    createBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Industry News' }])
  ];

  const industryLabel = industry ? INDUSTRIES.find(i => i.slug === industry)?.name || industry : '';

  return (
    <>
      <SEOHead
        title={`Industry News${industryLabel ? ` — ${industryLabel}` : ''} | LocalBranding Pro`}
        description="Expert guides, tips, and industry insights for local businesses. Learn how to get more customers with local SEO, reviews, and digital marketing."
        schema={schema}
      />

      <div className="min-h-screen" style={{ background: COLORS.light }}>
        {/* Nav */}
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="blog-nav-logo">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: COLORS.primary }}>
                <span className="text-white font-bold text-sm">LB</span>
              </div>
              <span className="font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>LocalBranding Pro</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/#features" className="text-sm text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/#pricing" className="text-sm text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/blog" className="text-sm font-medium" style={{ color: COLORS.primary }}>Industry News</Link>
              <Link to="/admin/login" className="text-sm text-slate-600 hover:text-slate-900">Log In</Link>
              <Link to="/admin/login" className="px-4 py-2 rounded-full text-sm font-semibold text-white" style={{ background: COLORS.primary }}>
                Start Free Trial
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="py-16 text-center">
          <div className="max-w-4xl mx-auto px-6">
            <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 inline-block" style={{ color: COLORS.primary }}>Industry News</span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              Local Marketing Insights
            </h1>
            <p className="text-lg text-slate-600" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Expert guides, pricing insights, and practical advice for local businesses and service professionals.
            </p>
          </div>
        </section>

        {/* Industry Filter */}
        {industries.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 mb-8">
            <div className="flex flex-wrap gap-2 items-center">
              <Tag className="h-4 w-4 text-slate-400" />
              <button
                onClick={() => { setIndustry(''); setPage(1); setSearchParams({}); }}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!industry ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
                data-testid="filter-all"
              >
                All
              </button>
              {industries.map(ind => (
                <button
                  key={ind.name}
                  onClick={() => { setIndustry(ind.name); setPage(1); setSearchParams({ industry: ind.name }); }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${industry === ind.name ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
                  data-testid={`filter-${ind.name}`}
                >
                  {(INDUSTRIES.find(i => i.slug === ind.name)?.name || ind.name).replace(/-/g, ' ')} ({ind.count})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Posts Grid */}
        <div className="max-w-7xl mx-auto px-6 pb-16">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-1/3" />
                    <div className="h-6 bg-slate-200 rounded w-full" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>No industry news yet.</p>
              <p className="text-slate-400 text-sm">Check back soon for local marketing insights and guides.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all border border-slate-100" data-testid={`blog-card-${post.slug}`}>
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.image_alt || post.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.primary }}>
                        {(INDUSTRIES.find(i => i.slug === post.industry)?.name || post.industry || '').replace(/-/g, ' ')}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Clock className="h-3 w-3" /> {post.read_time}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold mb-2 group-hover:text-blue-700 transition-colors" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
                      {post.title}
                    </h2>
                    <p className="text-sm text-slate-600 line-clamp-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-1 mt-3 text-sm font-medium" style={{ color: COLORS.primary }}>
                      Read <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > 12 && (
            <div className="flex justify-center gap-2 mt-12">
              {Array.from({ length: Math.ceil(total / 12) }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${page === i + 1 ? 'text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  style={page === i + 1 ? { background: COLORS.primary } : {}}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="py-8 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} LocalBranding Pro. All rights reserved.</p>
            <p className="text-xs text-slate-500">A <a href="https://web-techservices.com" className="text-slate-600 hover:text-slate-800 transition-colors">Web-TechServices AI</a> product.</p>
            <div className="flex gap-4 text-xs text-slate-500">
              <Link to="/" className="hover:text-slate-700">Home</Link>
              <Link to="/blog" className="hover:text-slate-700">Industry News</Link>
              <Link to="/admin/login" className="hover:text-slate-700">Admin Login</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
